import {
  Button,
  PHONE_COUNTRIES,
  PhoneInput,
  type Country,
} from "@/components";
import { PublicRoutes } from "@/constants/routes";
import { useForgotPassword } from "@/domain";
import { useToast } from "@/hooks";
import { useForgotPasswordPrefillStore } from "@/store/forgotPasswordPrefillStore";
import { phoneUtils } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

export const ForgotPasswordPhone = () => {
  // Lê o prefill vindo da tela de Auth (estado em memória, nunca persistido
  // em localStorage/cookies) uma única vez, sem efeito colateral no render.
  const prefillRef = useRef(useForgotPasswordPrefillStore.getState());

  const [country, setCountry] = useState<Country>(
    prefillRef.current.country ?? PHONE_COUNTRIES[0]
  );
  const [phone, setPhone] = useState(prefillRef.current.phone);

  useEffect(() => {
    useForgotPasswordPrefillStore.getState().clearPrefill();
  }, []);

  const { sendPhone, isPending } = useForgotPassword();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Brasil aceita fixo (10) ou celular (11); demais países usam só `length`.
  const minPhoneLength = country.minLength ?? country.length;
  const isPhoneComplete =
    phone.length >= minPhoneLength && phone.length <= country.length;

  const handleCountryChange = (next: Country) => {
    setCountry(next);
    setPhone(prev => prev.slice(0, next.length));
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const national = phoneUtils.cleanPhoneNumber(phone);
    // Brasil mantém o número nacional; demais países enviam DDI + número.
    const fullPhone =
      country.code === "BR" ? national : `${country.ddi}${national}`;

    sendPhone(
      { phone: fullPhone },
      {
        onSuccess: () => {
          navigate(
            `${PublicRoutes.AUTH_FORGOT_PASSWORD_CODE}?phone=${fullPhone}`
          );
        },
        onError: error => showToast(error.message, "error"),
      }
    );
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-black">Esqueci minha senha</h1>
        <p className="text-base text-gray-500">
          Enviaremos um código de 6 dígitos para o seu WhatsApp para redefinir a
          senha.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <PhoneInput
          label="Celular"
          autoFocus
          country={country}
          onCountryChange={handleCountryChange}
          value={phone}
          onChange={setPhone}
        />

        <Button type="submit" isLoading={isPending} disabled={!isPhoneComplete}>
          Receber código via WhatsApp
        </Button>
      </form>

      <p className="text-center text-sm text-gray-500">
        Lembrou a senha?{" "}
        <Link
          to={PublicRoutes.AUTH}
          className="cursor-pointer font-medium text-primary hover:underline"
        >
          Entrar com senha
        </Link>
      </p>
    </div>
  );
};
